import classNames from 'classnames';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const SCROLL_AMOUNT = 500;

export interface PillButtonOption {
  id: string;
  label: string;
  subLabel?: string;
}

export interface PillButtonsProps {
  options: PillButtonOption[];
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  buttonClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  selectedButtonClassName?: string;
  selectedSubtitleClassName?: string;
}

const PillButtons: React.FC<PillButtonsProps> = ({
  options,
  selectedId,
  onSelect,
  buttonClassName,
  titleClassName,
  subtitleClassName,
  selectedButtonClassName,
  selectedSubtitleClassName,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollWidth, clientWidth, scrollLeft } = el;
    const overflow = scrollWidth > clientWidth;
    setHasOverflow(overflow);
    setCanScrollLeft(overflow && scrollLeft > 0);
    setCanScrollRight(overflow && scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);
    el.addEventListener('scroll', updateScrollState);

    return () => {
      resizeObserver.disconnect();
      el.removeEventListener('scroll', updateScrollState);
    };
  }, [options.length, updateScrollState]);

  useLayoutEffect(() => {
    if (!selectedId) return;
    optionRefs.current.get(selectedId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }, [selectedId, options]);

  const scroll = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const delta = direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  if (!options?.length) {
    return null;
  }

  const arrowButtonClass =
    'shrink-0 flex h-9 w-9 items-center justify-center rounded-full border border-grey-4 bg-white text-grey-7 shadow-sm transition-colors duration-150 hover:bg-slate-1';

  const scrollContentClass = classNames(
    'flex gap-3 overflow-x-auto py-0',
    '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
  );

  return (
    <div className={classNames('-mx-4 flex min-w-0 items-center gap-2 px-4 lg:mx-0 lg:px-0', buttonClassName)}>
      {hasOverflow && canScrollLeft && (
        <button
          type="button"
          className={classNames(arrowButtonClass, 'hidden shrink-0 lg:flex')}
          onClick={() => scroll('left')}
          aria-label="Scroll left"
        >
          <i className="icon icon-chevron-down rotate-90 text-xl" />
        </button>
      )}
      <div
        ref={scrollRef}
        className={classNames(scrollContentClass, 'min-w-0 flex-1')}
        data-testid="pill-buttons-scroll"
      >
        {options.map((option) => {
          const isSelected = option.id === selectedId;

          const pillClasses = classNames(
            'shrink-0 rounded-full border px-4 py-2 text-left shadow-sm transition-colors duration-150',
            'flex flex-col justify-center min-w-[120px] cursor-pointer',
            isSelected ? selectedButtonClassName : 'bg-white border-grey-2 text-grey-7 hover:bg-slate-1'
          );

          const titleClasses = classNames('text-sm leading-tight', titleClassName);
          const subtitleClasses = classNames(
            'mt-0.5 text-xs',
            isSelected ? selectedSubtitleClassName : 'text-grey-5',
            subtitleClassName
          );

          return (
            <button
              key={option.id}
              ref={(node) => {
                if (node) {
                  optionRefs.current.set(option.id, node);
                } else {
                  optionRefs.current.delete(option.id);
                }
              }}
              type="button"
              className={pillClasses}
              onClick={() => onSelect?.(option.id)}
              aria-pressed={isSelected}
            >
              <span className={titleClasses}>{option.label}</span>
              {option.subLabel && <span className={subtitleClasses}>{option.subLabel}</span>}
            </button>
          );
        })}
      </div>
      {hasOverflow && canScrollRight && (
        <button
          type="button"
          className={classNames(arrowButtonClass, 'hidden shrink-0 lg:flex')}
          onClick={() => scroll('right')}
          aria-label="Scroll right"
        >
          <i className="icon icon-chevron-down -rotate-90 text-xl" />
        </button>
      )}
    </div>
  );
};

export default PillButtons;
